import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { DSText, DSSpacer } from '@zoe/ds-web'

const GraphCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 24px 20px 16px;
`

const SVG = styled.svg`
  display: block;
  width: 100%;
  height: auto;
`

const FootnoteRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
`

export type AnimatedScoreGraphProps = {
  startValue: number
  endValue: number
  startDateLabel?: string
  endDateLabel?: string
  durationMs?: number
}

// Wiggly path that starts at startY, ends at endY, with small mid-flight oscillations
// to match Figma's hand-drawn-feel curve.
const buildWigglyPath = (startY: number, endY: number, x0: number, x1: number) => {
  const total = endY - startY
  const span = x1 - x0
  // Six anchor points along x, with each y offset slightly above/below the linear interpolation
  // to create the wiggle. Negative offset = higher on screen.
  const stops = [
    { x: 0, dy: 0 },
    { x: 0.18, dy: -3 },
    { x: 0.36, dy: 6 },
    { x: 0.55, dy: -2 },
    { x: 0.75, dy: 4 },
    { x: 0.9, dy: -2 },
    { x: 1, dy: 0 },
  ]
  return stops
    .map((s, i) => {
      const x = x0 + s.x * span
      const linearY = startY + total * s.x
      const y = linearY + s.dy
      const prev = stops[i - 1]
      if (!prev) return `M ${x} ${y}`
      // Smooth curve using quadratic bezier with the previous midpoint as control
      const prevX = x0 + prev.x * span
      const prevLinearY = startY + total * prev.x
      const prevY = prevLinearY + prev.dy
      const cx = (prevX + x) / 2
      const cy = (prevY + y) / 2 - 2
      return `Q ${cx} ${cy}, ${x} ${y}`
    })
    .join(' ')
}

export const AnimatedScoreGraph: React.FC<AnimatedScoreGraphProps> = ({
  startValue,
  endValue,
  startDateLabel,
  endDateLabel,
  durationMs = 2400,
}) => {
  const pathRef = useRef<SVGPathElement>(null)
  const [animated, setAnimated] = useState(false)
  const [resolvedStartLabel, setResolvedStartLabel] = useState(startDateLabel ?? '')
  const [resolvedEndLabel, setResolvedEndLabel] = useState(endDateLabel ?? '')

  useEffect(() => {
    setAnimated(true)
    if (!startDateLabel || !endDateLabel) {
      const now = new Date()
      const end = new Date(now)
      end.setDate(end.getDate() + 42) // 6 weeks
      const fmt = (d: Date) => `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' }).toUpperCase()}`
      if (!startDateLabel) setResolvedStartLabel(fmt(now))
      if (!endDateLabel) setResolvedEndLabel(fmt(end))
    }
  }, [startDateLabel, endDateLabel])

  const width = 320
  const height = 170
  const bubbleHalfWidth = 22
  const bubbleHalfHeight = 16
  // Margin between each bubble's edge and the chart edge so the bubbles render
  // in full instead of being clipped by the SVG viewBox.
  const bubbleMargin = 8

  // Pin the lower (start) value to the bottom row and the higher (end) value
  // to the top row so the improvement reads as a steep climb regardless of the
  // numeric delta between the two scores.
  const startY = height - bubbleHalfHeight - bubbleMargin
  const endY = bubbleHalfHeight + bubbleMargin
  const xStart = bubbleHalfWidth + bubbleMargin
  const xEnd = width - bubbleHalfWidth - bubbleMargin
  const path = buildWigglyPath(startY, endY, xStart, xEnd)

  const dashLength = 900
  return (
    <>
      <GraphCard>
        <SVG viewBox={`0 0 ${width} ${height}`}>
          {/* Horizontal grid lines: five evenly spaced rules from the end-bubble
            row down to the start-bubble row (the bubbles cover the lines at
            their position, the rest of each line stays visible). */}
          {[0, 0.25, 0.5, 0.75, 1].map((t) => {
            const y = endY + t * (startY - endY)
            return <line key={t} x1={0} x2={width} y1={y} y2={y} stroke="#EDE8DE" strokeWidth={1} />
          })}
          {/* Yellow wiggly line */}
          <path
            ref={pathRef}
            d={path}
            fill="none"
            stroke="#FFD100"
            strokeWidth={5}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={dashLength}
            strokeDashoffset={animated ? 0 : dashLength}
            style={{ transition: `stroke-dashoffset ${durationMs}ms ease-out` }}
          />
          {/* Start value bubble */}
          <g transform={`translate(${xStart}, ${startY})`}>
            <rect x={-22} y={-16} width={44} height={32} rx={16} fill="#FFD100" />
            <text x={0} y={6} textAnchor="middle" fontSize="14" fontWeight="700" fill="#0E0F0C">
              {startValue}
            </text>
          </g>
          {/* End value bubble (fades in once line draw completes) */}
          <g
            transform={`translate(${xEnd}, ${endY})`}
            style={{ opacity: animated ? 1 : 0, transition: `opacity 200ms ease-out 600ms` }}
          >
            <rect x={-22} y={-16} width={44} height={32} rx={16} fill="#FFD100" />
            <text x={0} y={6} textAnchor="middle" fontSize="14" fontWeight="700" fill="#0E0F0C">
              {endValue}
            </text>
          </g>
        </SVG>
        <FootnoteRow>
          <DSText variant="fluid-label-100" weight="semiBold">
            {resolvedStartLabel}
          </DSText>
          <DSText variant="fluid-label-100" weight="semiBold">
            {resolvedEndLabel}
          </DSText>
        </FootnoteRow>
      </GraphCard>
      <DSSpacer size={8} direction="vertical" />
      {/* Disclaimer renders outside the white graph card and sits on the page
          background in the placeholder colour. */}
      <DSText variant="fluid-label-100" weight="regular" align="center" color="placeholder">
        For illustrative purposes only. Results may vary.
      </DSText>
    </>
  )
}
